import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { WaiterService } from '../_service/waiter.service';
import { CreateCartComponent } from '../common/create-cart/create-cart.component';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';

@Component({
  selector: 'app-create-order',
  standalone: true,
  imports: [RouterModule, CommonModule, NgbModule, FormsModule, CreateCartComponent, CommonModule],
  templateUrl: './create-order.component.html',
  styleUrl: './create-order.component.scss',
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 }))
      ])
    ]),
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class CreateOrderComponent implements OnInit {
  colorClasses = ['bg-light-red', 'bg-light-green', 'bg-light-yellow', 'bg-light-brown'];
  private modalService = inject(NgbModal);
  categories: any[] = [];
  tableValue: string | null = null;
  orderDetails: any;
  barcodeList: any;

  // Search related properties
  searchTerm: string = '';
  searchResults: any[] = [];
  isLoading: boolean = false;
  showDropdown: boolean = false;
  private searchSubject = new Subject<string>();

  // Loading states
  isPageLoading: boolean = true;
  isCartLoading: boolean = false;
  isCategoriesLoading: boolean = true;

  // Cart state
  cartItemCount: number = 0;
  constructor(
    private app: WaiterService, private route: ActivatedRoute, private router: Router) {
    // Initialize search observer
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        const storedArray = localStorage.getItem('orders') ? JSON.parse(localStorage.getItem('orders')!) : null;

        if (!term.trim()) {
          this.showDropdown = false;
          return of([]);
        }
        this.isLoading = true;
        return this.app.searchInventory(term, storedArray.barcode_id);
      })
    ).subscribe({
      next: (results) => {
        this.searchResults = results;
        this.isLoading = false;
        this.showDropdown = true;
        if (this.searchTerm == '') {
          this.showDropdown = false;
        }
      },
      error: (error) => {
        console.error('Search error:', error);
        this.isLoading = false;
      }
    });
  }
  ngOnInit(): void {
    this.fetchAllCategories();
    this.listBarcodes();
    this.initializeOrderDetails();
    this.updateCartCount();

    // Simulate loading completion
    setTimeout(() => {
      this.isPageLoading = false;
    }, 800);
  }

  private initializeOrderDetails(): void {
    const storedArray = localStorage.getItem('orders');
    if (storedArray) {
      this.orderDetails = JSON.parse(storedArray);
    }
  }

  private updateCartCount(): void {
    // Update cart count from localStorage or service
    const cartItems = localStorage.getItem('cartItems');
    if (cartItems) {
      const items = JSON.parse(cartItems);
      this.cartItemCount = items.length || 0;
    }
  }
  cart(content: any) {
    this.isCartLoading = true;
    this.updateCartCount();

    setTimeout(() => {
      this.isCartLoading = false;
      this.modalService.open(content, {
        centered: true,
        size: 'lg',
        backdrop: 'static'
      }).result.then(
        (result) => {
          this.updateCartCount();
        },
        (dismissed) => {
          this.updateCartCount();
        }
      );
    }, 500);
  }

  getRandomColorClass(index: number): string {
    return this.colorClasses[index % this.colorClasses.length]; // Cycles through color classes
  }
  fetchAllCategories() {
    this.isCategoriesLoading = true;
    this.app.fetchAllCategories().subscribe({
      next: (res: any) => {
        this.categories = res;
      },
      error: (err: any) => {
        console.log(err);
      },
      complete: () => {
        this.isCategoriesLoading = false;
      }
    });
  }

  listBarcodes() {
    this.app.listBarcodes().subscribe({
      next: (res: any) => {
        this.barcodeList = res
      },
      error: (err) => {
        console.log(err)
      },
    })
  }
  getBarcodeName(id: any) {
    let result;
    if (this.barcodeList) {
      result = this.barcodeList.find((item: any) => item.uid === id);
    }
    return result?.name;
  }
  selectItem(item: any): void {
    this.searchTerm = item.name;
    this.showDropdown = false;
    if (item && item.category_id) {
      this.router.navigate(['/create-order/categories/', item.category_id]);
    }
    this.searchTerm = '';
    this.showDropdown = false;
    this.searchResults = [];
    // Additional logic for selection...
  }

  // c// Add to your component class
  closeDropdown(): void {
    this.showDropdown = false;
    this.searchResults = [];
  }

  // Update onSearch method
  onSearch(event: any): void {
    const term = event.target.value;
    this.searchTerm = term;

    if (term.trim()) {
      this.showDropdown = true;
      this.searchSubject.next(term);
    } else {
      this.showDropdown = false;
      this.searchResults = [];
    }
  }

  onSearchFocus(): void {
    if (this.searchTerm.trim()) {
      this.showDropdown = true;
    }
  }

  onSearchBlur(): void {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
      this.showDropdown = false;
    }, 200);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchResults = [];
    this.showDropdown = false;
  }

  trackByItemId(index: number, item: any): any {
    return item.id || index;
  }

  trackByCategoryId(index: number, category: any): any {
    return category.id || index;
  }

  onImageError(event: any): void {
    event.target.src = 'icons/default-product.svg';
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }

}
